import { useCallback, useState } from 'react'
import { toast } from 'sonner'

import { translateText } from '@/api/api'
import { SegmentResponse } from '@/api/type'
import { SILENCE_TEXT } from '@/lib/constants'

type UseTranslationProps = {
  transcription: { segments: SegmentResponse[] } | null
}

type UseTranslationReturn = {
  translatedTexts: Map<number, string>
  translatingSegmentIndex: number | null
  showTranslationDialog: boolean
  translationDialogSegmentIndex: number | null
  fullTextTranslation: string | null
  fullTextOriginal: string | null
  isTranslatingFullText: boolean
  showFullTextTranslation: boolean
  handleTranslateSegment: (index: number, e: React.MouseEvent) => Promise<void>
  closeTranslationDialog: () => void
  handleTranslateFullText: () => Promise<void>
  setShowTranslationDialog: (show: boolean) => void
}

/**
 * 翻訳機能を管理するカスタムフック
 * セグメント単位の翻訳と全文翻訳を提供
 */
export const useTranslation = ({ transcription }: UseTranslationProps): UseTranslationReturn => {
  const [translatedTexts, setTranslatedTexts] = useState<Map<number, string>>(new Map())
  const [translatingSegmentIndex, setTranslatingSegmentIndex] = useState<number | null>(null)
  const [showTranslationDialog, setShowTranslationDialog] = useState(false)
  const [translationDialogSegmentIndex, setTranslationDialogSegmentIndex] = useState<number | null>(null)
  const [fullTextTranslation, setFullTextTranslation] = useState<string | null>(null)
  const [fullTextOriginal, setFullTextOriginal] = useState<string | null>(null)
  const [isTranslatingFullText, setIsTranslatingFullText] = useState(false)
  const [showFullTextTranslation, setShowFullTextTranslation] = useState(false)

  // 翻訳ボタンのクリックハンドラー
  const handleTranslateSegment = useCallback(
    async (index: number, e: React.MouseEvent) => {
      e.stopPropagation()
      if (!transcription) return

      const segment = transcription.segments[index]
      if (segment.text === SILENCE_TEXT) return

      // 既に翻訳済みの場合はダイアログを表示
      if (translatedTexts.has(index)) {
        setTranslationDialogSegmentIndex(index)
        setShowTranslationDialog(true)
        return
      }

      // 翻訳を実行
      setTranslatingSegmentIndex(index)
      try {
        const result = await translateText({
          text: segment.text,
          target_lang: 'ja',
        })

        setTranslatedTexts((prev) => {
          const newMap = new Map(prev)
          newMap.set(index, result.text)
          return newMap
        })

        setTranslationDialogSegmentIndex(index)
        setShowTranslationDialog(true)
      } catch (error) {
        console.error('Translation error:', error)
        toast.error('翻訳に失敗しました')
      } finally {
        setTranslatingSegmentIndex(null)
      }
    },
    [transcription, translatedTexts],
  )

  // 翻訳ダイアログを閉じる
  const closeTranslationDialog = useCallback(() => {
    setShowTranslationDialog(false)
    setTranslationDialogSegmentIndex(null)
  }, [])

  // 全文翻訳ハンドラー
  const handleTranslateFullText = useCallback(async () => {
    if (!transcription) return

    // 全てのセグメントのテキストを結合（無音とスキップを除外）
    const fullText = transcription.segments
      .filter((segment) => !(segment.text === SILENCE_TEXT || segment.skipped))
      .map((segment) => segment.text)
      .join(' ')

    if (!fullText.trim()) {
      toast.error('翻訳するテキストがありません')
      return
    }

    // 既に翻訳済みで、元のテキストと同じ場合はトグル動作
    if (fullTextTranslation && fullTextOriginal === fullText) {
      setShowFullTextTranslation((prev) => !prev)
      return
    }

    // 新規翻訳または再翻訳
    setIsTranslatingFullText(true)
    try {
      const result = await translateText({
        text: fullText,
        target_lang: 'ja',
      })

      setFullTextTranslation(result.text)
      setFullTextOriginal(fullText)
      setShowFullTextTranslation(true)
    } catch (error) {
      console.error('Translation error:', error)
      toast.error('翻訳に失敗しました')
    } finally {
      setIsTranslatingFullText(false)
    }
  }, [transcription, fullTextTranslation, fullTextOriginal])

  return {
    translatedTexts,
    translatingSegmentIndex,
    showTranslationDialog,
    translationDialogSegmentIndex,
    fullTextTranslation,
    fullTextOriginal,
    isTranslatingFullText,
    showFullTextTranslation,
    handleTranslateSegment,
    closeTranslationDialog,
    handleTranslateFullText,
    setShowTranslationDialog,
  }
}
